import React from "react";
import {
  Checkbox,
  DropDown,
  NumberInput,
  TextInput,
} from "@spg/component_library";
import "./ParameterForm.css";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegisterReturn,
} from "react-hook-form";
import {useIntl} from "react-intl";
import {SomeJSONSchema} from "ajv/dist/types/json-schema";

type ParameterFormInputProps = {
  fieldName: string;
  control: Control;
  register: (
    name: string,
    options?: RegisterOptions<FieldValues, string> | undefined,
  ) => UseFormRegisterReturn<string>;
  field: SomeJSONSchema["properties"][string];
  required:
    | {
        value: boolean;
        message: string;
      }
    | undefined;
  errors: FieldErrors;
  defaultValues: Record<string, string | number | boolean | undefined>;
  disabled: boolean | undefined;
  dirtyFields: Record<string, boolean>;
};

export function ParameterFormDropDown({
  fieldName,
  control,
  field,
  required,
  errors,
  defaultValues,
  disabled,
  dirtyFields,
}: ParameterFormInputProps) {
  const {formatMessage} = useIntl();

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{
        required,
      }}
      render={({field: {value, onChange}}) => (
        <DropDown
          inputId={fieldName}
          name={field.title}
          label={field.title}
          placeholder={formatMessage({
            id: "appInstallationParameters.ParameterForm.placeholder.select",
          })}
          supportingText={errors[fieldName]?.message?.toString()}
          isEdited={
            defaultValues[fieldName] != undefined && dirtyFields[fieldName]
          }
          isError={!!errors[fieldName]}
          required={!!required}
          isDisabled={disabled}
          options={(field.enum || []).map(
            (value: boolean | string | number) => ({
              value,
              label: "" + value,
            }),
          )}
          value={value}
          onChange={onChange}
        />
      )}
    />
  );
}

export function ParameterFormTextInput({
  fieldName,
  register,
  field,
  required,
  errors,
  defaultValues,
  disabled,
  dirtyFields,
}: ParameterFormInputProps) {
  const {formatMessage} = useIntl();
  return (
    <TextInput
      id={fieldName}
      label={field.title}
      supportingText={errors[fieldName]?.message?.toString()}
      isEdited={defaultValues[fieldName] != undefined && dirtyFields[fieldName]}
      isError={!!errors[fieldName]}
      isPassword={field.format === "password"}
      required={!!required}
      minLength={field.minLength}
      maxLength={field.maxLength}
      disabled={disabled}
      {...register(fieldName, {
        required,
        minLength: {
          value: field.minLength,
          message: formatMessage(
            {
              id: "appInstallationParameters.ParameterForm.error.tooShort",
            },
            {minLength: field.minLength},
          ),
        },
        maxLength: {
          value: field.maxLength,
          message: formatMessage(
            {
              id: "appInstallationParameters.ParameterForm.error.tooLong",
            },
            {maxLength: field.maxLength},
          ),
        },
        pattern: {
          value: new RegExp(field.pattern),
          message: formatMessage({
            id: "appInstallationParameters.ParameterForm.error.invalidPattern",
          }),
        },
      })}
    />
  );
}

export function ParameterFormCheckbox({
  fieldName,
  register,
  field,
  required,
  disabled,
}: ParameterFormInputProps) {
  return (
    <Checkbox
      id={fieldName}
      label={field.title}
      required={!!required}
      disabled={disabled}
      {...register(fieldName, {
        required,
      })}
    />
  );
}

export function ParameterFormNumberInput({
  fieldName,
  register,
  field,
  required,
  errors,
  defaultValues,
  disabled,
  dirtyFields,
}: ParameterFormInputProps) {
  const {formatMessage} = useIntl();

  return (
    <NumberInput
      id={fieldName}
      label={field.title}
      supportingText={errors[fieldName]?.message?.toString()}
      isEdited={defaultValues[fieldName] != undefined && dirtyFields[fieldName]}
      isError={!!errors[fieldName]}
      required={!!required}
      min={field.minimum}
      max={field.maximum}
      step={
        "multipleOf" in field
          ? field.multipleOf
          : field.type === "number"
            ? "any"
            : 1
      }
      disabled={disabled}
      {...register(fieldName, {
        required,
        valueAsNumber: true,
        min: {
          value: field.minimum,
          message: formatMessage(
            {
              id: "appInstallationParameters.ParameterForm.error.tooLow",
            },
            {min: field.minimum},
          ),
        },
        max: {
          value: field.maximum,
          message: formatMessage(
            {
              id: "appInstallationParameters.ParameterForm.error.tooHigh",
            },
            {max: field.maximum},
          ),
        },
      })}
    />
  );
}
